import { useState, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { cloneDeep } from "lodash";
import swal from "sweetalert";
import { Box, CircularProgress } from "@mui/material";
import axiosNoAuthInstance from "../../axiosApiNoAuth";
import DashboardMap from "./DashboardMap";
import {
  firmSizeRangeAtom,
  selectedFiltersAtom,
  selectedPlaceAtom,
  selectedSpatialLayerAtom,
  selectedSpatialValueAtom,
  smallBusinessMapChangeDataAtom,
  smallBusinessMapChangeModeAtom,
  smallBusinessMapHighlightAtom,
  smallBusinessMapZoomInAtom,
} from "../../../shared/state/atoms";
import { useGeoJSON } from "../../../shared/hooks";

// Small Business Change — duplicate of Intensity but shows Absolute Change or Percentage Change
// between a start year and end year, rather than "Percentage in each area" for a single year.
// Reuses the same MapExplorerViewSet with viewas=Absolute Change or Percentage Change.

// --- Shared helpers (same as SmallBusinessMapIntensity) ---

function resolveLayerConfig(selectedSpatialLayer, selectedPlace) {
  if (!selectedSpatialLayer || !selectedPlace) return null;
  const layerId = selectedSpatialLayer.id;
  const isMetroMuni = layerId === "municipality" && selectedPlace?.category === "A";

  if (isMetroMuni) {
    return {
      mode: "hex",
      taxdata_modelname: "hex7_fte_firmsizefte",
      spatial: "hex7",
      geoFile: "Metros_hex.geo.json",
      vget_field: "hex7",
      vpoly_field: "hex7",
      monoColour: false,
      metroForViewport: selectedPlace?.name,
      extraParams: { metro: selectedPlace?.name, cat_b: selectedPlace?.cat_b },
    };
  }

  if (layerId === "municipality") {
    return {
      mode: "allmunicipalities",
      taxdata_modelname: "metro_fte_firmsizefte",
      spatial: "allmunicipalities",
      geoFile: "LocalMunicipalities_2016.geo.json",
      vget_field: "cat_b",
      vpoly_field: "CAT_B",
      monoColour: false,
      metroForViewport: undefined,
      extraParams: {},
    };
  }

  if (layerId === "district") {
    return {
      mode: "alldistricts",
      taxdata_modelname: "metro_fte_firmsizefte",
      spatial: "alldistricts",
      geoFile: "DistrictMunicipalities_2018.geo.json",
      vget_field: "place_id",
      vpoly_field: "DISTRICT",
      monoColour: true,
      metroForViewport: undefined,
      extraParams: {},
    };
  }

  if (layerId === "province") {
    return {
      mode: "allprovinces",
      taxdata_modelname: "metro_fte_firmsizefte",
      spatial: "allprovinces",
      geoFile: "Province_2018.geo.json",
      vget_field: "place_id",
      vpoly_field: "PROVINCE",
      monoColour: true,
      metroForViewport: undefined,
      extraParams: {},
    };
  }

  return null;
}

function flattenCoords(geometry) {
  const coords = [];
  if (!geometry) return coords;
  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach((ring) => ring.forEach((c) => coords.push(c)));
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach((c) => coords.push(c))));
  }
  return coords;
}

function pointInRing(lng, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInGeometry(lng, lat, geometry) {
  if (!geometry) return false;
  if (geometry.type === "Polygon") {
    if (!pointInRing(lng, lat, geometry.coordinates[0])) return false;
    for (let h = 1; h < geometry.coordinates.length; h++) {
      if (pointInRing(lng, lat, geometry.coordinates[h])) return false;
    }
    return true;
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.some((poly) => {
      if (!pointInRing(lng, lat, poly[0])) return false;
      for (let h = 1; h < poly.length; h++) {
        if (pointInRing(lng, lat, poly[h])) return false;
      }
      return true;
    });
  }
  return false;
}

function verticalIntersections(lng, geometry) {
  const hits = [];
  const processRing = (ring) => {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [x1, y1] = ring[j];
      const [x2, y2] = ring[i];
      if ((x1 <= lng && x2 > lng) || (x2 <= lng && x1 > lng)) {
        const t = (lng - x1) / (x2 - x1);
        hits.push(y1 + t * (y2 - y1));
      }
    }
  };
  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach(processRing);
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((poly) => poly.forEach(processRing));
  }
  return hits;
}

function computeCentroid(feature) {
  const coords = flattenCoords(feature?.geometry);
  if (coords.length === 0) return null;
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  let cLng = (minLng + maxLng) / 2;
  let cLat = (minLat + maxLat) / 2;
  if (!pointInGeometry(cLng, cLat, feature.geometry)) {
    const hits = verticalIntersections(cLng, feature.geometry);
    if (hits.length > 0) {
      hits.sort((a, b) => Math.abs(a - cLat) - Math.abs(b - cLat));
      const nearest = hits[0];
      cLat = nearest > cLat ? nearest - 0.01 : nearest + 0.01;
    }
  }
  return { longitude: cLng, latitude: cLat, minLat, maxLat };
}

function computeClusterViewport(selectedFeature, allFeatures) {
  if (!selectedFeature || !allFeatures?.length) return computeZoomedViewport(selectedFeature);
  const selCoords = flattenCoords(selectedFeature.geometry);
  if (selCoords.length === 0) return null;
  let sMinLng = Infinity, sMaxLng = -Infinity, sMinLat = Infinity, sMaxLat = -Infinity;
  for (const [lng, lat] of selCoords) {
    if (lng < sMinLng) sMinLng = lng; if (lng > sMaxLng) sMaxLng = lng;
    if (lat < sMinLat) sMinLat = lat; if (lat > sMaxLat) sMaxLat = lat;
  }
  const lngPad = (sMaxLng - sMinLng) * 0.1 || 0.1;
  const latPad = (sMaxLat - sMinLat) * 0.1 || 0.1;
  let cMinLng = sMinLng, cMaxLng = sMaxLng, cMinLat = sMinLat, cMaxLat = sMaxLat;
  for (const f of allFeatures) {
    if (f === selectedFeature) continue;
    const coords = flattenCoords(f.geometry);
    if (coords.length === 0) continue;
    let fMinLng = Infinity, fMaxLng = -Infinity, fMinLat = Infinity, fMaxLat = -Infinity;
    for (const [lng, lat] of coords) {
      if (lng < fMinLng) fMinLng = lng; if (lng > fMaxLng) fMaxLng = lng;
      if (lat < fMinLat) fMinLat = lat; if (lat > fMaxLat) fMaxLat = lat;
    }
    if (fMaxLng >= sMinLng - lngPad && fMinLng <= sMaxLng + lngPad && fMaxLat >= sMinLat - latPad && fMinLat <= sMaxLat + latPad) {
      if (fMinLng < cMinLng) cMinLng = fMinLng; if (fMaxLng > cMaxLng) cMaxLng = fMaxLng;
      if (fMinLat < cMinLat) cMinLat = fMinLat; if (fMaxLat > cMaxLat) cMaxLat = fMaxLat;
    }
  }
  const span = Math.max(cMaxLng - cMinLng, cMaxLat - cMinLat);
  const zoom = Math.max(4, Math.min(12, Math.log2(360 / span) - 0.5));
  return { longitude: (cMinLng + cMaxLng) / 2, latitude: (cMinLat + cMaxLat) / 2, zoom, minZoom: zoom - 0.5, maxZoom: zoom + 2, pitch: 0, bearing: 0 };
}

function computeZoomedViewport(feature) {
  const coords = flattenCoords(feature?.geometry);
  if (coords.length === 0) return null;
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
  }
  const span = Math.max(maxLng - minLng, maxLat - minLat);
  const zoom = Math.max(4, Math.min(12, Math.log2(360 / span) - 0.5));
  return { longitude: (minLng + maxLng) / 2, latitude: (minLat + maxLat) / 2, zoom, minZoom: zoom - 0.5, maxZoom: zoom + 2, pitch: 0, bearing: 0 };
}

// --- End shared helpers ---

function SmallBusinessMapChange() {
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [selectedSpatialValue] = useAtom(selectedSpatialValueAtom);
  const [selectedFilters] = useAtom(selectedFiltersAtom);
  const [firmSizeRange] = useAtom(firmSizeRangeAtom);
  const [mapData, setMapData] = useAtom(smallBusinessMapChangeDataAtom);
  const [zoomIn] = useAtom(smallBusinessMapZoomInAtom);
  const [highlightOn] = useAtom(smallBusinessMapHighlightAtom);

  const [maxValue, setMaxValue] = useState(0);
  const [minValue, setMinValue] = useState(0);
  const [mapDataGrey, setMapDataGrey] = useState(null);
  const [reportKey, setReportKey] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [changeMode] = useAtom(smallBusinessMapChangeModeAtom);

  const layerConfig = resolveLayerConfig(selectedSpatialLayer, selectedPlace);
  const { geoData } = useGeoJSON(layerConfig?.geoFile ?? "Metros_hex.geo.json");

  // Reuse the same firmsize slider slot as Intensity (same range concept)
  const handles = firmSizeRange?.smallBusinessMapIntensity?.handles ?? [50];
  const inverted = firmSizeRange?.smallBusinessMapIntensity?.inverted ?? false;
  const firmsize_fte_lower = inverted ? handles[0] : 0;
  const firmsize_fte_upper = inverted ? 2000000 : handles[0];
  const isEmptyRange = firmsize_fte_lower === firmsize_fte_upper;

  // Clear map data when spatial layer changes
  const prevModeRef = useRef(layerConfig?.mode);
  useEffect(() => {
    if (layerConfig?.mode !== prevModeRef.current) {
      prevModeRef.current = layerConfig?.mode;
      setMapData(null);
      setMapDataGrey(null);
    }
  }, [layerConfig?.mode, setMapData]);

  useEffect(() => {
    if (!layerConfig || !geoData || !selectedSpatialValue || !selectedFilters?.startYear || !selectedFilters?.endYear) {
      // Prerequisites not ready yet (geoData still loading, etc.) — keep the spinner
      // visible rather than toggling to false and flashing the map container empty.
      setIsLoading(true);
      return;
    }

    if (isEmptyRange) {
      setMapData(null);
      setMapDataGrey(null);
      setIsLoading(false);
      setErrorMessage("No firm size range selected. Adjust the slider to select a range.");
      return;
    }

    const expectedField = layerConfig.vpoly_field;
    const sample = geoData?.features?.[0]?.properties;
    if (!sample || !(expectedField in sample)) return;

    async function getMapData() {
      setIsLoading(true);
      setErrorMessage(null);

      const vpoly = { type: "FeatureCollection", features: [] };
      const vpolyGrey = { type: "FeatureCollection", features: [] };

      let vmax = 0;
      let vmin = Number.MAX_SAFE_INTEGER;

      try {
        const params = {
          taxdata_modelname: layerConfig.taxdata_modelname,
          spatial: layerConfig.spatial,
          output: "fte",
          year: selectedFilters.startYear,
          secondyear: selectedFilters.endYear,
          viewas: changeMode,
          firmsize_fte_lower,
          firmsize_fte_upper,
          ...layerConfig.extraParams,
        };

        const response = await axiosNoAuthInstance.get("/api/map-explorer/", { params });

        if (!response.data || response.data.length === 0) {
          setIsLoading(false);
          setErrorMessage(`No data available for ${selectedFilters.startYear}–${selectedFilters.endYear}`);
          return;
        }

        for (let i = 0; i < response.data.length; i++) {
          for (let j = 0; j < geoData.features.length; j++) {
            if (geoData.features[j].properties[layerConfig.vpoly_field] === response.data[i][layerConfig.vget_field]) {
              const placeName = response.data[i].place_name || "";
              const showPlace = placeName && placeName !== "Not Used" && placeName !== "Not used";
              const tooltipLabel = (displayVal) => {
                const changeLabel = changeMode === "Percentage Change" ? "% FTE Change" : "Abs FTE Change";
                const lines = [
                  '<div style="font-size:12px">',
                  `<span style="font-weight:bold">${inverted ? "Medium-Large Business" : "Small Business"}</span>`,
                  `${changeLabel}: ${displayVal}`,
                ];
                if (showPlace) lines.push(`Place: ${placeName}`);
                lines.push('</div>');
                return lines;
              };
              if (typeof response.data[i].display !== "number") {
                const greyFeature = cloneDeep(geoData.features[j]);
                greyFeature.properties.value = "0";
                greyFeature.properties.label = tooltipLabel(response.data[i].display);
                vpolyGrey.features.push(greyFeature);
              } else {
                const feature = cloneDeep(geoData.features[j]);
                feature.properties.value = response.data[i].display;
                if (parseFloat(feature.properties.value) > vmax) vmax = parseFloat(feature.properties.value);
                if (parseFloat(feature.properties.value) < vmin) vmin = parseFloat(feature.properties.value);
                feature.properties.label = tooltipLabel(parseFloat(response.data[i].display.toFixed(2)));
                vpoly.features.push(feature);
              }
              continue;
            }
          }
        }

        if (vpoly.features.length === 0 && vpolyGrey.features.length === 0) {
          setIsLoading(false);
          setErrorMessage(`No data available for ${selectedFilters.startYear}–${selectedFilters.endYear}`);
          return;
        }

        setMaxValue(vmax);
        setMinValue(vmin === Number.MAX_SAFE_INTEGER ? 0 : vmin);
        setMapData(vpoly);
        setMapDataGrey(vpolyGrey);
        setReportKey((k) => k + 1);
        setIsLoading(false);
      } catch (error) {
        if (error?.response?.status === 404 || error?.response == null) {
          setIsLoading(false);
          setErrorMessage("No Data");
        } else {
          swal("Failed", `${error}`, "error", { timer: 4000, buttons: false });
          setIsLoading(false);
        }
      }
    }

    getMapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSpatialLayer,
    selectedPlace,
    selectedSpatialValue,
    selectedFilters?.startYear,
    selectedFilters?.endYear,
    changeMode,
    firmsize_fte_lower,
    firmsize_fte_upper,
    geoData,
  ]);

  if (!layerConfig) {
    return (
      <div className="flex">
        <div className="w-full">
          <div className="text-lg px-10 pt-5 font-sans font-bold">
            {selectedSpatialLayer?.name} / {selectedPlace?.name} / <span style={{ color: "#1976d2" }}>Small Business</span>
          </div>
          <div className="text-3xl font-bold px-10 mb-10 font-roboto">Change</div>
          <div className="px-10 text-gray-500 italic">Please complete the spatial selection on the left.</div>
        </div>
      </div>
    );
  }

  const yearLabel = selectedFilters?.startYear && selectedFilters?.endYear
    ? `${selectedFilters.startYear}–${selectedFilters.endYear}`
    : "";

  return (
    <div className="flex">
      <div className="w-full">
        <div className="text-lg px-10 pt-5 font-sans font-bold">
          {selectedSpatialLayer?.name} / {selectedPlace?.name} / <span style={{ color: "#1976d2" }}>Small Business</span>
        </div>
        <div className="text-3xl font-bold px-10 mb-10 font-roboto">
          Change {yearLabel}
        </div>

        {errorMessage ? (
          <div className="p-20 flex justify-center">{errorMessage}</div>
        ) : (
          <div className="flex">
            <div className="w-[60%] ml-[20%]">
              {isLoading && (
                <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
                  <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
                    <CircularProgress color="success" size="3rem" />
                  </Box>
                </div>
              )}
              {mapData && mapData.features && mapData.features.length > 0 && (
                <DashboardMap
                  key={`change-${layerConfig.mode}-${layerConfig.geoFile}-${selectedSpatialValue}-${zoomIn ? "zoomed" : "country"}-${changeMode}`}
                  reportKey={reportKey}
                  data={mapData}
                  data_grey={mapDataGrey}
                  metro={layerConfig.metroForViewport}
                  viewAs={changeMode}
                  output="fte"
                  maxValue={maxValue}
                  minValue={minValue}
                  monoColour={layerConfig.monoColour}
                  customViewport={(() => {
                    if (layerConfig.mode === "hex") return undefined;
                    const baseVp = { longitude: 25, latitude: -29, zoom: 4.55, minZoom: 4.45, maxZoom: 4.65, pitch: 0, bearing: 0 };
                    const geoReady = geoData?.features?.[0]?.properties?.[layerConfig.vpoly_field] !== undefined;
                    if (zoomIn && geoReady && layerConfig.mode !== "allprovinces") {
                      const selectedFeature = geoData?.features?.find(
                        (f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue
                      );
                      if (layerConfig.mode === "allmunicipalities" || layerConfig.mode === "alldistricts") {
                        const clusterVp = computeClusterViewport(selectedFeature, geoData?.features);
                        return clusterVp || baseVp;
                      }
                      const zoomedVp = computeZoomedViewport(selectedFeature);
                      return zoomedVp || baseVp;
                    }
                    const placeCentroid = computeCentroid(
                      geoData?.features?.find(
                        (f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue
                      )
                    );
                    if (placeCentroid) {
                      const topEdge = baseVp.latitude + 9;
                      const bottomEdge = baseVp.latitude - 7;
                      const topHeadroom = 5.3;
                      const bottomHeadroom = 1.5;
                      let shift = 0;
                      if (placeCentroid.latitude > topEdge - topHeadroom) {
                        shift = placeCentroid.latitude - (topEdge - topHeadroom);
                      }
                      if (placeCentroid.minLat < bottomEdge + bottomHeadroom) {
                        const bottomShift = placeCentroid.minLat - (bottomEdge + bottomHeadroom);
                        if (shift === 0 || Math.abs(bottomShift) > Math.abs(shift)) shift = bottomShift;
                      }
                      if (shift !== 0) return { ...baseVp, latitude: baseVp.latitude + shift };
                    }
                    return baseVp;
                  })()}
                  highlightFeature={
                    highlightOn && layerConfig.mode !== "hex"
                      ? geoData?.features?.find(
                          (f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue
                        ) || null
                      : null
                  }
                  pinnedTooltip={(() => {
                    if (layerConfig.mode === "hex" || !highlightOn) return null;
                    const geoFeature = geoData?.features?.find(
                      (f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue
                    );
                    if (!geoFeature) return null;
                    const centroid = computeCentroid(geoFeature);
                    if (!centroid) return null;
                    const dataFeature =
                      mapData?.features?.find((f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue) ||
                      mapDataGrey?.features?.find((f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue);
                    if (!dataFeature?.properties?.label) return null;
                    const labels = dataFeature.properties.label.filter(
                      (l) => !l.startsWith("<div") && !l.startsWith("</div")
                    );
                    const text = labels.map((l) => l.replace(/<[^>]*>/g, "")).join("\n");
                    return { ...centroid, text };
                  })()}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SmallBusinessMapChange;
